import { useEffect, useState, useCallback } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { Droplet, Utensils, Syringe, Target, Clock, ArrowRight, Users } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useProfile } from "../context/ProfileContext";
import GlucoseChart from "../components/GlucoseChart";
import StatusBadge, { classifyGlucose } from "../components/StatusBadge";

function elapsed(min, t) {
  if (min == null || min < 0) return "";
  if (min < 60) return t("ago_min", { n: min });
  return t("ago_hour", { n: Math.round(min / 60) });
}

function StatCard({ Icon, label, value, unit, sub, badge, testid }) {
  return (
    <div className="df-card df-card-hover p-4 min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-[#627D98] uppercase tracking-wide min-w-0 break-words">{label}</span>
        <span className="h-8 w-8 shrink-0 rounded-lg bg-[#E3EFF9] flex items-center justify-center"><Icon size={16} className="text-[#1A73E8]" /></span>
      </div>
      <div className="mt-2 flex flex-wrap items-baseline gap-1">
        <span className="font-head text-2xl font-bold text-[#0D253F]">{value}</span>
        {unit && <span className="text-sm text-[#627D98]">{unit}</span>}
      </div>
      {badge && <div className="mt-1.5">{badge}</div>}
      {sub && <div className="mt-1 text-xs text-[#627D98] break-words">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { t } = useI18n();
  const { activeProfileId, activeProfile, can } = useProfile();
  const canLog = ["log_meals", "log_glucose", "log_doses"].some((p) => can(p));
  const { openRegister } = useOutletContext();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!activeProfileId) return;
    const { data } = await api.get(`/profiles/${activeProfileId}/dashboard`);
    setData(data);
  }, [activeProfileId]);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("df-refresh", h);
    return () => window.removeEventListener("df-refresh", h);
  }, [load]);

  if (!data) return <div className="text-[#627D98]">{t("loading")}</div>;

  const g = data.last_glucose;
  const unit = activeProfile?.glucose_unit || "mg/dL";
  const gStatus = g ? classifyGlucose(g.value, g.unit, data.target) : null;

  return (
    <div className="space-y-5">
      {/* Greeting */}
      <div className="df-card p-6 relative overflow-hidden" style={{ background: "linear-gradient(135deg,#1A73E8,#0D3C78)" }}>
        <div className="relative z-10">
          <h1 className="font-head text-2xl sm:text-3xl font-bold text-white">
            {t("hi")}, {activeProfile?.preferred_name} 👋
          </h1>
          <p className="text-blue-100 mt-1">{t("dash_sub")}</p>
          {canLog && (
          <button onClick={openRegister} data-testid="dash-register-btn" className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-[#1A73E8] px-4 py-2 font-semibold text-sm active:scale-95 transition">
            <Droplet size={16} /> {t("register_action")}
          </button>
          )}
        </div>
        <div className="absolute -right-10 -bottom-10 h-48 w-48 rounded-full bg-white/10" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard testid="stat-last-glucose" Icon={Droplet} label={t("last_glucose")}
          value={g ? g.value : "—"} unit={g ? g.unit : t("no_reading")}
          badge={gStatus && <StatusBadge status={gStatus} />}
          sub={g ? `${new Date(g.datetime).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} · ${g.source === "sensor" ? t("src_sensor") : t("src_capillary")} ${elapsed(g.elapsed_minutes, t)}` : t("not_confirmed_zero")} />
        <StatCard testid="stat-carbs" Icon={Utensils} label={t("carbs_today")}
          value={data.carbs_today} unit={t("grams")}
          sub={`${data.meals_count} ${t("meals_count")}`} />
        <StatCard testid="stat-insulin" Icon={Syringe} label={t("insulin_today")}
          value={data.has_doses_today ? data.insulin_today : "—"} unit={data.has_doses_today ? t("units") : ""}
          sub={data.has_doses_today ? t("applied_registered") : t("day_not_zero")} />
        <StatCard testid="stat-target" Icon={Target} label={t("target_range")}
          value={data.target ? `${data.target.lower}–${data.target.upper}` : "—"} unit={data.target?.unit} />
      </div>

      {/* Chart + register */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="df-card p-5 lg:col-span-2 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-head font-semibold text-[#0D253F]">{t("glucose_today")}</h3>
            <Link to="/app/analise" className="text-sm text-[#1A73E8] font-medium flex items-center gap-1">{t("nav_analysis")} <ArrowRight size={14} /></Link>
          </div>
          <GlucoseChart readings={data.chart} target={data.target} unit={unit} />
          <p className="text-xs text-[#627D98] mt-2 flex items-center gap-1"><Clock size={12} /> {t("manual_note")}</p>
        </div>

        <div className="space-y-5 min-w-0">
          {canLog && (
          <div className="df-card p-5">
            <h3 className="font-head font-semibold text-[#0D253F] mb-3">{t("quick_register")}</h3>
            <div className="space-y-2">
              {[["glucose", Droplet, t("reg_glucose")], ["meal", Utensils, t("reg_meal")], ["dose", Syringe, t("reg_dose")]].map(([id, Icon, label]) => (
                <button key={id} onClick={openRegister} data-testid={`quick-${id}`} className="w-full flex items-center gap-3 rounded-xl border border-[#E3EFF9] px-3 py-2.5 text-sm font-medium text-[#0D253F] hover:bg-[#F4F8FC] transition">
                  <Icon size={18} className="text-[#1A73E8]" /> {label}
                </button>
              ))}
            </div>
          </div>
          )}
          <div className="df-card p-5">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-head font-semibold text-[#0D253F]">{t("shared_routine")}</h3>
              <Users size={18} className="text-[#627D98]" />
            </div>
            <p className="text-sm text-[#627D98]">{data.family.members.length} {t("caregivers")}</p>
            <Link to="/app/familia" className="text-sm text-[#1A73E8] font-medium flex items-center gap-1 mt-2">{t("nav_family")} <ArrowRight size={14} /></Link>
          </div>
        </div>
      </div>

      {/* Recent meals */}
      <div className="df-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-head font-semibold text-[#0D253F]">{t("recent_meals")}</h3>
          <Link to="/app/refeicoes" className="text-sm text-[#1A73E8] font-medium">{t("see_all")}</Link>
        </div>
        {data.recent_meals.length === 0 ? (
          <p className="text-sm text-[#627D98]">{t("no_meals_today")}</p>
        ) : (
          <div className="divide-y divide-[#F0F5FA]">
            {data.recent_meals.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`recent-meal-${m.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="h-9 w-9 shrink-0 rounded-lg bg-[#E3EFF9] flex items-center justify-center"><Utensils size={16} className="text-[#1A73E8]" /></span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#0D253F] truncate">{m.notes || t("reg_meal")}</div>
                    <div className="text-xs text-[#627D98]">{new Date(m.datetime).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                </div>
                <span className="text-sm font-semibold text-[#0D253F] shrink-0">{m.total_carbs} {t("grams")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
